import { TRANSPORT_MESSAGES, classifyTransport } from '../../../shared/api/transportErrorMessages.ts';
/**
 * Admin API 失败提示统一映射（让用户明白失败原因，而非直透后端 error / 裸异常消息）。
 *
 * 数据流契约（对齐 `api/client.ts`）：
 * - `ApiResult.ok=false` 时 `code` 为后端 Result 信封业务码（如 AUTH_403 / 4506 / 3410）；
 *   `error` 为后端 message（或 HTTP 提示）。
 * - `code === undefined` 表示传输层失败：error 形态有浏览器网络异常消息
 *   （Chrome "Failed to fetch" / Safari "Load failed" / FF "NetworkError..."）、
 *   'network_error'、'invalid_json'（HTTP ok 但 JSON 解析失败）、`HTTP <status>`、
 *   纯数字 status（无信封 code 的非 2xx）。client 无显式 timeout 机制，
 *   Abort 类消息按超时防御性归类。
 *
 * 收录原则：只收 admin 门户实际消费的 API 域会返回的高频码（真源
 * `allinks-common/.../ErrorCode.java`，只读摘录），禁止全量搬运。
 * 未收录 code 一律原样返回 fallback（后端 message），不吞错、不虚构。
 * admin 无 i18n 体系，用户可见文案硬编码简体中文为门户既有惯例。
 */

/** 业务码 → 用户可行动中文（原因 + 下一步） */
const CODE_MESSAGES: Readonly<Record<string, string>> = {
  // ---- 通用层（鉴权 / 校验 / 限频 / 并发 / 服务可用性）----
  AUTH_401: '登录状态已失效，请重新登录',
  AUTH_401_X: '登录已过期，请重新登录',
  AUTH_401_C: '账号或密码错误',
  AUTH_401_T: '认证凭证无效，请重新登录',
  AUTH_401_CH: '认证渠道与当前门户不匹配，请从正确的入口登录',
  AUTH_401_D: '账号已被禁用，请联系平台管理员',
  AUTH_403: '当前账号无权执行该操作，如需开通请联系平台管理员',
  VAL_400: '请求参数校验失败，请检查表单内容后重试',
  VALIDATION_ERROR: '请求参数校验失败，请检查表单内容后重试',
  NOT_FOUND: '请求的资源不存在，请刷新页面后重试',
  '42901': '操作过于频繁，请稍后再试',
  RATE_LIMIT: '操作过于频繁，请稍后再试',
  '1008': '同一操作正在处理中，请勿重复提交',
  '1009': '请求与首次提交内容不一致，请刷新页面后重试',
  CONC_409: '数据已被他人修改，请刷新页面后重试',
  SM_422: '对象当前状态不允许该流转，请刷新后重试',
  SVC_503: '服务暂不可用，请稍后重试',
  SVC_503_STUB: '该功能暂未开放，请联系平台管理员',

  // ---- MFA / OTP（3410/3411/3412/3415 由 OtpConfirmDialog 优先分流，此处为无弹窗宿主兜底）----
  '3410': '2FA 验证码错误，请重新输入',
  '3411': '2FA 验证码尝试过于频繁，请稍后再试',
  '3412': '请先绑定 2FA（两步验证）后再执行敏感操作',
  '3413': '2FA 绑定会话已过期，请重新发起绑定',
  '3414': '2FA 已绑定，请先解绑后再操作',
  '3415': 'MFA 服务暂不可用，请稍后再试',
  '3416': '请先修改登录密码后再继续',
  '3417': '开户资料待完善，请先完成开户资料提交',
  MFA_PASSWORD_REQUIRED: '请输入登录密码',
  MFA_PASSWORD_INCORRECT: '密码错误',
  PASSWORD_REQUIRED: '请输入密码',
  PASSWORD_INCORRECT: '密码错误',

  // ---- 商户 / 代理（merchants / merchantCredentials / agents / platformAccounts）----
  MCH_404: '商户不存在或已被删除，请刷新列表后重试',
  MCH_405: '商户证照不存在，请确认证照已上传',
  MCH_406: '证照类型不合法，请检查后重新上传',
  MCH_409: '商户当前状态不允许该操作，请先检查商户状态',
  AGT_404: '代理商不存在或已被删除，请刷新列表后重试',
  AGT_422: '代理商当前状态不允许该操作，请先检查代理商状态',
  PTU_404: '门户登录账号不存在（未开户或已注销），请确认账号信息',

  // ---- 卡片（cards）----
  CARD_404: '卡片不存在或已被删除，请刷新后重试',
  CARD_409: '卡片当前状态不允许该操作，请先检查卡片状态',
  CARD_402: '卡内余额不足，请先充值后再试',
  CARD_407: '该卡无欠缴款项，无需补扣',
  CARD_422: '卡片敏感信息数据不完整，请联系平台客服',
  // 422 家族已拆码（A5）：按精确码分别提示，禁止再用裸 422 判断
  CARD_REVEAL_422: '仅已激活的虚拟卡可查看完整卡号',
  MFA_422: '请先绑定 2FA（两步验证）后再执行敏感操作',
  CARD_LIVENESS_422: '该卡片须先完成活体验证（商户强制活体）',
  CARD_3302: '该持卡人开卡张数已达上限',
  CARD_4303: '卡片短码号段已用尽，需联系平台人工扩位',

  // ---- KYC / 开户（kyc / onboarding / merchantCreateOtp）----
  KYC_404: 'KYC 申请不存在或已被处理，请刷新后重试',
  KYC_409: 'KYC 申请当前状态不允许该操作，请刷新后重试',
  KYC_403: '该操作须持卡人本人完成（认证层资料不可代填）',
  '3301': '持卡人尚未完成身份认证（KYC），请先完成认证',
  '3302': 'KYC 补件次数已达上限，请重新发起申请',
  '3303': '已通过活体认证，无需重复认证',
  '3420': '商户存在未缴清的 KYC 巡检罚款，暂不可开卡',
  ONB_404: '开户申请不存在或已被处理，请刷新列表后重试',
  '3201': '开户必填资料缺失，请补全后重新提交',
  '3202': '当日开户申请提交已达上限，请明日再试',
  '3203': '该注册号已存在有效商户，请勿重复开户',
  '3205': '该法人 90 天内禁止再次申请开户',
  '3206': '代理商当前状态不可提交开户申请',
  '3207': '开户补件次数已达上限',
  '3208': '审核清单未完成，禁止通过，请先完成必检项',
  '3209': '该代理尚未获得产品授权，请先申请产品授权',
  '3210': '请先为该代理配置至少 1 个产品授权价',

  // ---- 费项 / 费率（feeItems / rates / rateCenter / rateBaselines）----
  FEE_404: '费项不存在或未启用，请刷新费项目录后重试',
  FEE_400: '费项售价不得低于成本价，请调整后重试',
  FEE_402: '二次计费扣款失败（余额不足），请稍后重试',
  FEE_409: '同维度费项已存在生效行，请停用旧行或改为编辑',
  FEE_423: '费项变更冷却中，请稍后再试',
  '1001': '新配置费项必须挂接产品，请先选择产品',
  '3101': '费率低于下层成本价，请上调后重试',
  '3102': '费率超过平台上限，请下调后重试',
  '3103': '费率变更冷却中，请稍后再试',
  '3104': '商户不属于该代理或当前状态不可配置费率',
  '3105': '产品未授权给该代理，请先完成产品授权',
  RATE_CALC_400: '费率计算器参数非法（金额/币种），请检查后重试',

  // ---- 卡产品（products）----
  PROD_404: '卡产品不存在或已被删除，请刷新后重试',
  PROD_409: '卡产品编码已存在，请更换编码',
  PROD_410: '产品编号已达上限，需联系平台人工扩位',
  PRODUCT_BIN_REQUIRED: '卡产品未配置 BIN，请先在通道侧完成 BIN 配置',
  PRODUCT_CHANNEL_REQUIRED: '卡产品未配置默认渠道，请先绑定渠道',
  PRODUCT_CHANNEL_BIN_MISMATCH: '产品默认渠道与 BIN 路由胜出渠道不一致，请检查配置',

  // ---- 上游渠道（channels / channelMappings / channelWorkbench）----
  CHN_404: '上游渠道不存在，请刷新后重试',
  CHN_409: '发卡通道拒绝该操作，请核对渠道状态与参数',
  CHN_409_CODE: '渠道编码已存在，请更换编码',
  CHN_502: '发卡通道调用失败，请稍后重试或联系渠道方',
  CHN_MAP_404: '渠道字段映射不存在，请刷新后重试',
  CHN_CFG_404: '通道配置项不存在，请刷新后重试',
  CHN_CFG_422: '敏感配置禁止明文写入，请使用加密引用',
  CHN_BAL_404: '通道余额快照不存在，请刷新后重试',
  CHN_LOG_404: '通道调用日志不存在，请刷新后重试',
  CHN_SM_404: '通道子商户不存在，请刷新后重试',
  CHN_SM_409: '同一归属在该通道已有子商户，请勿重复创建',
  CHN_SM_422: '子商户当前状态不允许该操作，请先检查状态',
  CHN_BIN_404: 'BIN 路由配置不存在，请刷新后重试',
  NO_BIN_ROUTE: '无匹配的启用 BIN 路由，请先配置 BIN 路由',
  CHN_PM_404: '通道产品映射不存在，请刷新后重试',
  CHN_PM_409: '通道产品映射已存在（渠道+产品+币种+卡型），请勿重复配置',
  CHANNEL_DISABLED: '渠道已停用，禁止新开卡',
  CHANNEL_MAINTENANCE: '渠道维护中，禁止新开卡，请稍后再试',
  CHANNEL_ENABLE_PRECHECK_FAILED: '渠道启用门禁未通过（配置列缺项），请补全后重试',
  BIN_OVERLAP: 'BIN 区间/前缀与已有启用配置重叠，请调整区间',

  // ---- 金融 / 结算 / 备付 / 头寸（finance / settlement / escrow / forceRefunds / reconciliation / position*）----
  SET_402: '待结算金额（pending）不足，无法过账',
  SET_404: '结算批次不存在，请刷新后重试',
  SET_409: '结算批次状态不允许该操作（或该日批次已完成），请刷新后重试',
  STMT_404: '对账单不存在或无权访问，请刷新后重试',
  INV_422: '开票金额超过可开票额度，请调整后重试',
  INV_403: '专票申请缺少一般纳税人资质，请先上传资质',
  REC_404: '对账差异工单不存在，请刷新后重试',
  REC_409: '对账差异工单当前状态不允许该操作，请刷新后重试',
  FRF_404: '强制退款单不存在，请刷新后重试',
  FRF_400: '强制退款资金科目非法，请检查科目配置',
  FRF_409: '强制退款单当前状态不允许该操作，请刷新后重试',
  FRF_403: '退款金额超阈值，须财务 L3 会签后再提交',
  FRF_402: '运营资金不足，无法过账，请稍后重试',
  ESC_3501: '备付合规限制（黄档）：禁止大额提现',
  ESC_3502: '备付合规限制（红档）：禁止新授权',
  ESC_3503: '备付合规限制（红档）：禁止新开卡',
  POS_404: '头寸账户/充值单/调回单不存在，请刷新后重试',
  POS_409: '头寸相关单据当前状态不允许该操作，请刷新后重试',
  '3005': '存在回冲欠款，暂不可提现',
  '3006': '提现科目非法，请检查后重试',
  '3401': '可调回额度不足，请核对额度后再试',
  '3402': '商户当前状态不允许调回，请先检查商户状态',
  '3404': '调回单当前状态不允许撤销，请刷新后重试',
  '3405': '金额低于最低充值/调回限额，请调整后重试',
  '3406': '可划转额度不足，请核对额度后再试',
  '3407': '目标持卡人状态不允许（未 KYC / 非活跃 / 风控标记）',
  '3408': '头寸账户当前状态不允许该操作（冻结/关闭）',
  MCC_404: '划转单不存在或无权访问，请刷新后重试',

  // ---- 风控（risk / logAnalysis）----
  '6001': '风控规则不存在，请刷新后重试',
  '6002': '风控规则编码已存在，请更换编码',
  '6003': '平台基线规则参数不可修改（仅状态可调）',
  '6004': '规则状态不允许该转换，请刷新后重试',
  '6005': '规则未通过预检，不可直接上线（请先跑 SHADOW 预检）',
  '6006': '高危操作需勾选二次确认后再提交',
  '6007': '缺少 2FA 验证码，请重新提交',
  '6009': '名单条目已存在（幂等跳过）',
  '6010': 'kill-switch 仅平台超管可执行',
  '6011': '拦截事件当前状态不允许该处置，请刷新后重试',
  '6012': '基线规则不可覆盖（由平台统一下发）',
  '6013': '回滚目标版本不存在，请刷新后重试',
  '6014': '拦截事件不存在，请刷新后重试',
  '6015': '覆盖阈值格式不合法，请检查后重试',
  '6016': '批量处置目标数超过单次上限 50，请分批操作',
  '6017': '处置目标不属于当前商户，请刷新后重试',
  '6018': '用户风险状态不允许该转换，请刷新后重试',
  '6019': '本商户覆盖项已达 50 条上限，请先停用部分覆盖',
  RFG_404: '欺诈团伙不存在，请刷新后重试',
  RFG_409: '欺诈团伙当前状态不允许该操作，请刷新后重试',
  AML_404: 'AML 监管案件不存在，请刷新后重试',
  AML_409: 'AML 案件当前状态不允许该操作，请刷新后重试',
  DSP_404: '争议工单不存在，请刷新后重试',
  DSP_409: '争议工单当前状态不允许该操作，请刷新后重试',
  LOG_404: '日志异常记录不存在，请刷新后重试',
  LOG_409: '日志异常当前状态不允许确认/处置，请刷新后重试',

  // ---- 运营（emailOps / webhook / reports / files）----
  MAIL_404: '邮件相关对象不存在，请刷新后重试',
  MAIL_403: '无权执行该邮件操作（或禁止转发上游原文）',
  MAIL_409: '邮件当前状态不允许该操作，请刷新后重试',
  MAIL_502: '白标邮件发送失败，请稍后重试',
  MAIL_CH_404: '邮件通道不存在，请先创建通道',
  MAIL_CH_422: '邮件通道测试失败，配置未保存，请检查参数',
  WH_400: 'webhook 配置参数非法（URL / 事件类型 / 数量超限）',
  WH_403: '无权订阅该类 webhook',
  WH_404: 'webhook endpoint / delivery 不存在，请刷新后重试',
  WH_409: 'delivery 当前状态不允许重发（仅失败/中止状态可重发）',
  RPT_403: '报表类型不在白名单，请选择已开放的报表',
  RPT_404: '报表不存在，请刷新后重试',
  FILE_404: '文件不存在或已过期（导出文件仅保留 7 天）',
  EXPORT_502: '导出邮件发送失败，请稍后重试',

  // ---- 实体卡物流 / 制卡（physicalCards）----
  PCH_BATCH_404: '实体卡批次不存在，请刷新后重试',
  PCH_BATCH_409: '批次号已存在，请更换批次号',
  '7006': 'CSV 行数超限，请分批导入',
  '7007': '卡号 Luhn 校验失败，请检查 CSV 卡号',
  '7008': 'BIN 段与产品不匹配，请检查导入数据',
  '7010': 'CSV 文件为空，请检查后重新上传',
  '7020': '制卡批次不存在，请刷新后重试',
  '7021': '同批次已有非取消状态的生产单，请勿重复下单',
  '7022': '下单数量必须大于 0',
  '7023': '生产单当前状态不允许该操作，请刷新后重试',
  '7024': '2FA 验证码缺失或无效，请重新输入',
  PROD_ORD_404: '制卡生产单不存在，请刷新后重试',

  // ---- 代登录 / 门户（portalImpersonation）----
  '3428': '代登录票据无效，请回到运营后台重新发起',
  '3429': '代登录票据已过期，请回到运营后台重新发起',
  '3430': '代登录票据已被使用，请回到运营后台重新发起',
  '3431': '门户域名未配置，请先在「平台基础设置」中配置',
  '3432': '代登录会话不允许执行账号安全或凭据类操作',
};

/**
 * 统一失败提示入口：业务码 → 可行动中文；未收录 code → fallback 原样；
 * code === undefined → 按传输层形态归类（网络异常 / 超时 / 服务不可用 / 未知）。
 *
 * 绝不吞错：所有分支都有输出；fallback 缺失时给通用兜底。
 */
export function mapApiError(code: string | undefined, fallback: string): string {
  if (code !== undefined && code !== '') {
    if (CODE_MESSAGES[code]) {
      return CODE_MESSAGES[code];
    }
    return fallback && fallback.trim() ? fallback : TRANSPORT_MESSAGES.unknown;
  }
  const classified = classifyTransport(fallback ?? '');
  if (classified !== TRANSPORT_MESSAGES.unknown) {
    return classified;
  }
  return fallback && fallback.trim() ? fallback : TRANSPORT_MESSAGES.unknown;
}

/**
 * catch 分支兜底：意外异常（含 api 层抛出的业务失败 Error）转用户可读文案。
 * api 层 `throw` 携带 `code` 属性时走业务码映射；否则按传输形态归类，
 * 未识别的技术消息不裸透给用户。
 */
export function mapThrownError(error: unknown, fallback?: string): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    const message = (error as { message?: unknown }).message;
    if (typeof code === 'string') {
      return mapApiError(code, typeof message === 'string' && message ? message : (fallback ?? ''));
    }
  }
  const message = error instanceof Error ? error.message : '';
  const classified = classifyTransport(message);
  if (classified !== TRANSPORT_MESSAGES.unknown) {
    return classified;
  }
  return fallback ?? TRANSPORT_MESSAGES.unknown;
}

/** 仅供测试/守护脚本检视收录面 */
export function apiErrorCodeCount(): number {
  return Object.keys(CODE_MESSAGES).length;
}

/** 仅供测试/守护脚本检视收录面（码表键；守护键 ⊆ ErrorCode.java 码值集合） */
export function apiErrorCodeKeys(): readonly string[] {
  return Object.freeze([...Object.keys(CODE_MESSAGES)]);
}
